// Mock data for the exec dashboard prototype.
// Sources:
//   - HealthyMind_2026_Plan_V10.xlsx (revenue & forecast model)
//   - exec-dashboard-spec-v0.3.html (KPI shape + page structure)
//   - אפיון.docx (8 sections)
//
// All "ביצוע" (actual) numbers for May are still forecast values since real
// May data is not yet in BigQuery. Q1 (Jan-Mar) values are true actuals.
//
// Swap to real API once endpoints are wired up.

// Period semantics — snapshot-first per Ohad's design call (2026-06-01):
//   now   = live state, "right now" snapshot (active patients, current wait, etc.)
//   month = last completed calendar month
//   quarter = last completed calendar quarter
//   year  = last completed calendar year (or YTD until year ends)
export type Period = 'now' | 'month' | 'quarter' | 'year';

export type TopicId = 'financial' | 'patients' | 'staff' | 'meetings' | 'quality';

export interface KpiCard {
  id: string;
  topic: TopicId;
  label: string;
  value: string;
  target?: string;
  forecast?: string;
  pillStatus?: 'good' | 'bad' | 'warn';
  pillText?: string;
  missing?: boolean;
  missingReason?: string;
  needsDecision?: boolean;
  drillTo?: string;
  fillPct?: number;            // 0-1, drives the vertical mini-bar height
  fillStatus?: 'good' | 'warn' | 'bad';  // colors the bar regardless of height
  // Chart-tile only — extra line under the headline (e.g. "צפי EoM 102%" for newPatients per Avi #5)
  extraNote?: string;
  // Definition text shown inside the chart-tile tooltip. Useful when the tile
  // label is shortened ("מטופלים חדשים + פעילים") and the reader needs the
  // precise meaning of each status. Added 2026-06-07.
  tooltipDefinition?: string;
  // Secondary value rendered next to the main value (small, on the left side
  // of the big number). Per Ohad 2026-06-07 — used on No Show tiles to surface
  // the absolute count alongside the headline %.
  auxValue?: string;
}

export interface ChartMonth {
  label: string;
  target: number;
  actual?: number;             // only Jan-Mar have real actuals
  forecast?: number;           // Apr-Dec are forecast from Excel V10
  coordinated?: number;        // current month: MAX coordinated = done + scheduled_forward (raw)
}

export interface FunnelStage {
  label: string;
  count: number;
  pctOfPrev?: string;
}

export interface SectionMeta {
  num: number;
  title: string;
  shows: string;
  source: string;
  formula: string;
  missing: string;
  status: 'ready' | 'partial' | 'blocked';
  statusLabel: string;
  drillTo?: string;
}

export const mockExec = {
  asOfMonth: 'נכון לעכשיו',
  asOfDate: '2026-06-01',
  defaultPeriod: 'now' as Period,

  // KPI layout per Avi's 13-item refinement (2026-06-04). Items 1/2/4/5/7/11 → chart tile,
  // items 6/8/9/10/13 → simple tile, items 3/12 removed.
  kpis: [
    // --- financial (Avi #1, #2 — both line charts) ---
    {
      id: 'revenue', topic: 'financial',
      label: 'הכנסות',
      value: '', // chart-tile reads from revenueChart
      drillTo: '/exec/financial',
    },
    {
      id: 'expenses', topic: 'financial',
      label: 'הוצאות',
      value: '',
      drillTo: '/exec/financial',
    },

    // --- patients (Avi #4, #5 — both line charts) ---
    //
    // CANONICAL patient-status definitions (Ohad 2026-06-07, pending company sign-off):
    //   Pre     — questionnaire filled → CM intake complete
    //   New     — CM intake complete   → Psy intake complete
    //   Active  — Psy intake complete  → treatment end
    //   Closing — treatment end (follow-ups)
    //
    // Panel 3 — activePatients = distinct patients with a DONE meeting that month.
    //   "How many patients did we actually treat this month?"
    {
      id: 'activePatients', topic: 'patients',
      label: 'מטופלים שטופלו (בחודש)',
      value: '',
      tooltipDefinition:
        'מספר המטופלים שהייתה להם לפחות פגישה אחת שבוצעה (done) באותו חודש — ' +
        'כל סוג פגישה, כל מטופל נספר פעם אחת.\n' +
        'שונה מסך הפגישות: מטופל יכול להגיע לכמה פגישות באותו חודש.',
      drillTo: '/exec/patients',
    },
    // Panel 4 — newPatients = FLOW per month of Pre → New transitions.
    //   "How many patients finished CM intake this month?"
    //   Broader than the old "STRICT" def (which also required entry-this-month).
    //   Shachar view: count of CM intakes completed in the calendar month,
    //   regardless of when the patient entered Pre.
    {
      id: 'newPatients', topic: 'patients',
      label: 'מטופלים שסיימו אינטייק מנהל תיק',
      value: '',
      tooltipDefinition:
        'רק מי שסיים אינטייק CM החודש (עבר מ-Pre לחדש).\n' +
        'לא כולל מי שתקוע ב-Pre או מחכה כבר חודשיים לאינטייק פסיכיאטר.',
      drillTo: '/exec/patients',
    },

    // --- staff (Avi #7 chart + chart, Avi #6 + #13 simple) ---
    {
      id: 'staffHeadcount', topic: 'staff',
      label: 'מצבת פסיכיאטרים',
      value: '',
      drillTo: '/exec/staff',
    },
    {
      id: 'psyHours', topic: 'staff',
      label: 'שעות פתוחות / פסיכ׳',
      value: '',
      extraNote: 'ממוצע חודשי · ניתנו מול תחזית',
      drillTo: '/exec/staff',
    },
    {
      id: 'psyUtilization', topic: 'staff',
      label: 'ניצולת פסיכיאטרים',
      value: '87%',
      target: '85%',
      pillStatus: 'good',
      pillText: 'מעל יעד',
      fillPct: 0.92, fillStatus: 'good',
      drillTo: '/exec/staff',
    },
    {
      // Added 2026-06-07 per Ohad — mirror tile to psyUtilization for CM.
      id: 'cmUtilization', topic: 'staff',
      label: 'ניצולת CM',
      value: '78%',
      target: '85%',
      pillStatus: 'warn',
      pillText: 'מתחת ליעד',
      fillPct: 0.78, fillStatus: 'warn',
      drillTo: '/exec/staff',
    },
    {
      // ratio14 → moved visually to the Meetings group per Ohad 2026-06-07.
      // Topic stays 'meetings' so the colored border matches the meetings block.
      id: 'ratio14', topic: 'meetings',
      // Avi #13: "אחוז עמידה ביעד 1 לארבעה. מאוד חשוב"
      label: 'יחס CM:Psy',
      value: '2.0:1',
      target: '4:1 (Blueprint)',
      forecast: 'דצמ׳ V10: 3.4:1',
      pillStatus: 'warn',
      pillText: 'חצי מהיעד',
      needsDecision: true,
      fillPct: 0.50, fillStatus: 'warn',
      drillTo: '/exec/patients#ratio',
    },

    // --- meetings (No Show + next-available-slot wait per Ohad 2026-06-07) ---
    // Note: meetingsDone tile removed — backlog/throughput moves to drill.
    // Loss calc on the No Show tiles (forecast field) is a placeholder string.
    // When wired to live data: compute as Σ (no_show_count × actual_price) over
    // (role × type × hmo) — NOT a blended average — and surface here. The drill
    // shows the 4-way matrix; this main tile just sums it up.
    {
      // No Show% + absolute count in the period (small aux next to the big %).
      id: 'noShowPsy', topic: 'meetings',
      label: 'No Show פסיכ׳',
      value: '18%',
      auxValue: '143 פגישות',
      target: '<10%',
      pillStatus: 'bad',
      pillText: 'מעל יעד',
      needsDecision: true,
      fillPct: 0.45, fillStatus: 'bad',
      drillTo: '/exec/meetings',
    },
    {
      id: 'noShowCM', topic: 'meetings',
      label: 'No Show CM',
      value: '8%',
      auxValue: '89 פגישות',
      target: '<10%',
      pillStatus: 'good',
      pillText: 'ביעד',
      fillPct: 0.88, fillStatus: 'good',
      drillTo: '/exec/meetings',
    },
    {
      // Per Ohad 2026-06-07: this tile is the FORWARD-looking "next free slot",
      // NOT the historical avg wait. Backlog data lives in the drill.
      // Shachar's view needs: MIN(meeting.start_time) where status=open + therapist
      // role + (now + capacity logic). Mock value below is a placeholder.
      id: 'firstWaitPsy', topic: 'meetings',
      label: 'סלוט פסיכ׳ הבא',
      value: '23 ימים',
      target: '<14',
      forecast: 'דרוש view משחר',
      pillStatus: 'bad',
      pillText: 'מעל יעד',
      needsDecision: true,
      fillPct: 0.35, fillStatus: 'bad',
      drillTo: '/exec/meetings',
    },
    {
      id: 'firstWaitCM', topic: 'meetings',
      label: 'סלוט CM הבא',
      value: '5 ימים',
      target: '<14',
      forecast: 'דרוש view משחר',
      pillStatus: 'good',
      pillText: 'ביעד',
      fillPct: 0.85, fillStatus: 'good',
      drillTo: '/exec/meetings',
    },

    // --- quality (Avi #11 — multi-month line) ---
    {
      id: 'satisfaction', topic: 'quality',
      label: 'שביעות רצון · 1-10',
      value: '',
      extraNote: 'יעד 8.5 · 2 ירידות (פבר׳ + מאי) · ראה דריל לפיצול פר שאלה',
      drillTo: '/exec/satisfaction',
    },
  ] as KpiCard[],

  // Revenue forecast vs actual — net revenue from Excel V10 Revenue Model
  // Jan-Mar = actuals (from BQ via Excel), Apr-May = current forecast
  // Apr-Dec listed but render shows Jan-Jun in 6-col chart per v0.3
  revenueChart: [
    { label: 'ינו׳', target: 380000, actual: 365538 },
    { label: 'פבר׳', target: 470000, actual: 468055 },
    { label: 'מרץ',  target: 650000, actual: 683152 },
    { label: 'אפר׳', target: 720000, actual: 715294 },
    { label: 'מאי',  target: 900000, actual: 898781 },
    { label: 'יוני', target: 1100000, forecast: 1122203 },
  ] as ChartMonth[],

  // Expenses — mock placeholder pending real numbers from finance.
  expensesChart: [
    { label: 'ינו׳', target: 320000, actual: 295000 },
    { label: 'פבר׳', target: 360000, actual: 358000 },
    { label: 'מרץ',  target: 410000, actual: 425000 },
    { label: 'אפר׳', target: 440000, actual: 432000 },
    { label: 'מאי',  target: 500000, actual: 465000 },
    { label: 'יוני', target: 540000, forecast: 528000 },
  ] as ChartMonth[],

  // Panel 3 — active patients = SNAPSHOT (New ∪ Active) at month boundary.
  // Last entry = current month, partial-month count, % displayed pro-rated.
  // Target column = V10 plan. Current month uses `actual` (partial accumulated)
  // — the forecast field is unused here per Ohad 2026-06-07 (target+actual only).
  activePatientsChart: [
    { label: 'ינו׳', target: 2000, actual: 1850 },
    { label: 'פבר׳', target: 2400, actual: 2317 },
    { label: 'מרץ',  target: 2800, actual: 2362 },
    { label: 'אפר׳', target: 3300, actual: 3201 },
    { label: 'מאי',  target: 4200, actual: 4404 },
    { label: 'יוני', target: 5100, actual: 1190 },
  ] as ChartMonth[],

  // Panel 4 — new patients = FLOW per month, count of CM intakes completed.
  // Per Ohad 2026-06-07: dropped the old "entered this month" constraint —
  // anyone whose CM intake completed in the month counts (Pre → New transition).
  // Last entry = current month, partial-month count, % displayed pro-rated.
  newPatientsChart: [
    { label: 'ינו׳', target: 250, actual: 220 },
    { label: 'פבר׳', target: 300, actual: 287 },
    { label: 'מרץ',  target: 350, actual: 358 },
    { label: 'אפר׳', target: 400, actual: 412 },
    { label: 'מאי',  target: 450, actual: 430 },
    { label: 'יוני', target: 500, actual: 118 },
  ] as ChartMonth[],

  // Staff panels — main exec page, 4 numbers per type (Ohad 2026-06-08).
  // Headcount = active therapists in current month.
  // Hours = total opened in calendar this month.
  // Utilization = booked ÷ opened.
  // Next free slot = earliest available slot across all therapists of that type.
  // 33 CMs is the real count from the rates file; 17 psy from V10 May.
  staffPanels: {
    asOfMonth: 'יוני 2026',
    psy: { headcount: 17, totalHours: 580, utilization: 0.88, nextFreeSlotDays: 4 },
    cm:  { headcount: 33, totalHours: 1980, utilization: 0.85, nextFreeSlotDays: 2 },
  },

  // Avi #11 — satisfaction trend (1-10 scale). REAL numbers pulled from BQ
  // 2026-06-07 — DWH.Satisfaction_Questionnaire monthly AVG across 4 closed
  // questions. Has visible volatility (Feb + May dips to 8.34) that earlier
  // smoothed mock missed.
  satisfactionChart: [
    { label: 'ינו׳', target: 8.5, actual: 8.92 },
    { label: 'פבר׳', target: 8.5, actual: 8.34 },
    { label: 'מרץ',  target: 8.5, actual: 8.81 },
    { label: 'אפר׳', target: 8.5, actual: 8.70 },
    { label: 'מאי',  target: 8.5, actual: 8.34 },
    { label: 'יוני', target: 8.5, forecast: 8.98 },
  ] as ChartMonth[],

  // Section 3 — patient funnel per v0.3 (mock — real numbers in BQ later)
  funnel: [
    { label: 'פניות חדשות', count: 320 },
    { label: 'Intake שנקבע', count: 210, pctOfPrev: '66%' },
    { label: 'Intake שבוצע', count: 175, pctOfPrev: '83%' },
    { label: 'טיפול ראשון', count: 150, pctOfPrev: '86%' },
    { label: 'מטופלים פעילים', count: 128, pctOfPrev: '85%' },
  ] as FunnelStage[],

  // All 8 sections from the spec with build-readiness from v0.3
  sections: [
    {
      num: 1,
      title: 'תקציר הנהלה',
      shows: 'רצועת מדדי על — יעד · ביצוע · תחזית · פער.',
      source: 'מצרף מסעיפים 2-8; יעדים ממודל forcast_2026.',
      formula: 'פער = ביצוע − יעד (או ביצוע − תחזית).',
      missing: 'הכרעת רשימת מדדי העל; הגדרת חישוב הפער.',
      status: 'partial',
      statusLabel: 'חלקי · תלוי הכרעה',
    },
    {
      num: 2,
      title: 'הכנסות ורווחיות',
      shows: 'הכנסה ורווח חודשיים, תחזית מול ביצוע, פילוח קופה/קליניקה/סגמנט.',
      source: 'תחזית: forcast_2026 (8 סגמנטים). ביצוע: Meeting + Patient.funding_entity. הכנסה בפועל ב-DWH.Deals בלבד.',
      formula: 'תחזית = פגישות × תעריף. ביצוע = פגישות שבוצעו × תעריף בפועל − עלות.',
      missing: 'תעריף+עלות פר-פגישה, הכנסה בפועל, שדה גיל (Adult/Youth), בסיס NewPatients.',
      status: 'blocked',
      statusLabel: 'חסום · צד הביצוע',
    },
    {
      num: 3,
      title: 'משפך מטופלים',
      shows: 'פנייה → Intake נקבע → Intake בוצע → טיפול ראשון → פעיל, עם אחוזי המרה.',
      source: 'Patient.created_at, Meeting (type 1/2), Meeting.status.',
      formula: 'המרה בין שלבים = שלב N ÷ שלב N-1.',
      missing: 'רגע קביעת ה-Intake (תאריך סיום שלב).',
      status: 'ready',
      statusLabel: 'מוכן ל-v1',
      drillTo: '/',
    },
    {
      num: 4,
      title: 'No Show',
      shows: 'שיעור לפי תחום/יום/שעה, מגמה חודשית, השפעה כספית.',
      source: 'Meeting.status (noshow/canceled/done), Meeting.start, user.role.',
      formula: 'No Show% = פגישות noshow ÷ פגישות מתוכננות.',
      missing: 'הגדרת No Show מול ביטול + חותמת זמן ביטול; תעריף לפגישה.',
      status: 'ready',
      statusLabel: 'מוכן ל-v1 · שיעור',
      drillTo: '/meetings',
    },
    {
      num: 5,
      title: 'ניצולת מטפלים',
      shows: 'שעות מאוישות מול זמינות, ניצולת לפי סוג מטפל.',
      source: 'מאוישות: Meeting × משך. סוג: user.role. זמינות: —',
      formula: 'ניצולת = שעות מאוישות ÷ שעות זמינות.',
      missing: 'טבלת שעות זמינות, חופשות, הגדרת ניצולת.',
      status: 'blocked',
      statusLabel: 'חסום · אין זמינות',
    },
    {
      num: 6,
      title: 'תחזית עומסים',
      shows: 'ביקוש צפוי מול קיבולת זמינה, זיהוי מחסור/עודף.',
      source: 'ביקוש: forcast_2026 + Patient.next_meeting_start. קיבולת: תלוי זמינות.',
      formula: 'פער קיבולת = ביקוש צפוי − קיבולת זמינה.',
      missing: 'קיבולת זמינה (תלוי סעיף 5), אופק תחזית, סף מחסור/עודף.',
      status: 'blocked',
      statusLabel: 'חסום · תלוי קיבולת',
    },
    {
      num: 7,
      title: 'גיוס מטפלים',
      shows: 'מצבת מול יעד, סטטוס משרות, זמן גיוס ממוצע.',
      source: 'מצבת: ספירת user לפי role. סטטוס/יעד/תאריכים: —',
      formula: 'זמן גיוס = תאריך איוש − תאריך פתיחת משרה.',
      missing: 'יעד מצבת, מקור גיוס (סטטוס משרה, תאריכים, שיוך לתחום).',
      status: 'blocked',
      statusLabel: 'חסום · אין מקור',
    },
    {
      num: 8,
      title: 'איכות שירות',
      shows: 'זמן המתנה לתור ראשון, SLA, שביעות רצון, תלונות.',
      source: 'המתנה: Patient.created_at → פגישה ראשונה. CSAT/תלונות: —',
      formula: 'זמן המתנה = פגישה ראשונה − created_at.',
      missing: 'יעד SLA, מקור CSAT, מקור תלונות.',
      status: 'partial',
      statusLabel: 'חלקי',
    },
  ] as SectionMeta[],
};
